import { FC } from "react";
import { StepsProps } from "../../types";
import { Button } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import useStep4 from "./useStep4";
import { FormInput, FormSectionHeader, Loader } from "@/components";
import {
  AP_INVOICE,
  BLANKET_LIMIT,
  BOARD_LEVEL_APPROVAL,
  BUDGET,
  CONTRACT,
  PM_APPROVAL_LIMIT,
  PM_YEARLY_LIMIT,
  SINGLE_APPROVAL,
  WORK_ORDER,
} from "./consts";

const Step4: FC<StepsProps> = ({
  onCancel,
  isView,
  onPreviousClick,
}) => {
  const {
    form,
    onSubmit,
    isUpdatePending,
    isEditDetails,
    id,
    isApprovalInfoLoading,
    isApprovalInfoRefetching,
  } = useStep4();
  const { errors } = form.formState;

  return isApprovalInfoLoading || isApprovalInfoRefetching ? (
    <Loader />
  ) : (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
          <FormSectionHeader>Approval Workflow</FormSectionHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
            <FormInput
              name={AP_INVOICE}
              control={form.control}
              label="AP Invoice Approvals"
              placeholder="0"
              error={errors[AP_INVOICE]?.message}
              isRequired
              disabled={isView}
              variant="integer"
              minLength={1}
            />
            <FormInput
              name={BUDGET}
              control={form.control}
              label="Budget Approvals"
              placeholder="0"
              error={errors[BUDGET]?.message}
              isRequired
              variant="integer"
              minLength={1}
              disabled={isView}
            />
            <FormInput
              name={CONTRACT}
              control={form.control}
              label="Contract Approvals"
              placeholder="0"
              error={errors[CONTRACT]?.message}
              isRequired
              variant="integer"
              minLength={1}
              disabled={isView}
            />
            <FormInput
              name={WORK_ORDER}
              control={form.control}
              label="Work Order Approvals"
              placeholder="0"
              error={errors[WORK_ORDER]?.message}
              isRequired
              variant="integer"
              minLength={1}
              disabled={isView}
            />
          </div>
        </div>
        <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
          <FormSectionHeader>Invoice Approval Threshold</FormSectionHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
            <FormInput
              name={BLANKET_LIMIT}
              control={form.control}
              label="Auto Approval Blanket Limit"
              placeholder="0"
              error={errors[BLANKET_LIMIT]?.message}
              isRequired
              disabled={isView}
              variant="integer"
              minLength={1}
              info="Upper cap that doesn’t require any approval"
              startIcon="$"
            />
            <FormInput
              name={SINGLE_APPROVAL}
              control={form.control}
              label="Aggregated Single Approval Amount Limit"
              placeholder="0"
              error={errors[SINGLE_APPROVAL]?.message}
              isRequired
              variant="integer"
              minLength={1}
              disabled={isView}
              info="Upper cap that the individual board members & CAM can approve cumulatively"
              startIcon="$"
            />
            <FormInput
              name={BOARD_LEVEL_APPROVAL}
              control={form.control}
              label="Board-Level Approval Limit"
              placeholder="0"
              error={errors[BOARD_LEVEL_APPROVAL]?.message}
              isRequired
              variant="integer"
              minLength={1}
              disabled={isView}
              info="Upper cap that the board can approve"
              startIcon="$"
            />
          </div>
        </div>
        <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
          <FormSectionHeader>
            CAM Invoice Approval Setting
          </FormSectionHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
            <FormInput
              name={PM_APPROVAL_LIMIT}
              control={form.control}
              label="Per Invoice Limit"
              placeholder="0"
              error={errors[PM_APPROVAL_LIMIT]?.message}
              isRequired
              disabled={isView}
              variant="integer"
              minLength={1}
              info="Auto approval enabled, fallback to CAM if needed."
              startIcon="$"
            />
            <FormInput
              name={PM_YEARLY_LIMIT}
              control={form.control}
              label="Yearly Approval Limit"
              placeholder="0"
              error={errors[PM_YEARLY_LIMIT]?.message}
              isRequired
              variant="integer"
              minLength={1}
              disabled={isView}
              info="Max Limit for CAM in a year"
              startIcon="$"
            />
          </div>
        </div>
        <div className="flex flex-row justify-between items-center  py-3">
          {!id ? (
            <Button variant="outline" type="button" onClick={onPreviousClick}>
              Previous
            </Button>
          ) : (
            <div></div>
          )}
          <div className="flex flex-row justify-end gap-3  py-3">
            <Button variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
            {!isView && (
              <Button type="submit">
                {isUpdatePending && <Loader variant="secondary" />}
                {isEditDetails ? "Save" : "Save & Next"}
              </Button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Step4;
